import { Component, OnInit } from '@angular/core';
import { CommonApiService } from '../../common-api.service';
import { Permissions } from '../../auth-guard.guard';
import { LoginService } from '../../login/login.service';
import { Router } from '@angular/router';
import { ChartDataset, ChartOptions } from 'chart.js';

interface DeliveryItem {
  job_no: string;
  delivery_date: string;
  delivery_due: string;
  doctor_name: string;
}

interface DashboardSummary {
  counts: {
    doctors: number;
    products: number;
    employees: number;
    users: number;
    in_production: number;
    hold: number;
    repair: number;
    repeat: number;
    total_jobs_month: number;
    completed_month: number;
  };
  monthly_revenue: number;
  status_distribution: Record<string, number>;
  monthly_trend: { month: string; count: number }[];
  deliveries: {
    late: DeliveryItem[];
    today: DeliveryItem[];
    upcoming: DeliveryItem[];
    late_window_days?: number;
  };
  top_doctors: { doctor_id: number; name: string; business_amount: number; completed_cases: number }[];
  top_products: { product_id: number; name: string; category_name: string; total_units: number; cases_count: number }[];
}

interface ProductLegendItem {
  name: string;
  units: number;
  percentage: number;
  color: string;
}

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css']
})
export class DashboardComponent implements OnInit {
  lateDeliveryWindowOptions = [
    { value: 30, label: 'Last 30 Days' },
    { value: 60, label: 'Last 60 Days' },
    { value: 90, label: 'Last 90 Days' },
    { value: 120, label: 'Last 120 Days' },
    { value: 150, label: 'Last 150 Days' },
    { value: 180, label: 'Last 180 Days' },
    { value: 365, label: 'Last 1 Year' },
  ];
  selectedLateDeliveryWindow = 30;

  currentUser: any;
  userName = '';
  isAdmin = false;
  hasDashboardAccess = false;
  loading = true;
  today = new Date();

  // Stat counts
  doctorCount = 0;
  productCount = 0;
  userCount = 0;
  employeeCount = 0;
  inProductionJobsCount = 0;
  holdJobsCount = 0;
  repairJobsCount = 0;
  repeatJobsCount = 0;
  totalJobsMonth = 0;
  completedMonth = 0;
  monthlyRevenue = 0;

  // Delivery tables
  lateDeliveries: DeliveryItem[] = [];
  todayDeliveries: DeliveryItem[] = [];
  upcomingDeliveries: DeliveryItem[] = [];

  // Top doctors (by business amount, current month)
  topDoctors: { doctor_id: number; name: string; business_amount: number; completed_cases: number }[] = [];
  // Top products (by usage in done jobs, current month)
  topProducts: { product_id: number; name: string; category_name: string; total_units: number; cases_count: number }[] = [];
  productLegendItems: ProductLegendItem[] = [];
  readonly productPalette = [
    '#4f46e5', '#22c55e', '#f59e0b', '#ef4444', '#06b6d4',
    '#a855f7', '#ec4899', '#14b8a6', '#84cc16', '#f97316',
  ];

  // ── Pie chart: Top product usage ──
  productPieLabels: string[] = [];
  productPieDatasets: ChartDataset<'pie'>[] = [{ data: [] }];
  productPieOptions: ChartOptions<'pie'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const value = Number(ctx.parsed) || 0;
            const dataArr = (ctx.dataset.data || []) as number[];
            const total = dataArr.reduce((a, b) => a + (Number(b) || 0), 0);
            const pct = total ? Math.round((value / total) * 100) : 0;
            return `${ctx.label} - ${value} units (${pct}%)`;
          },
        },
      },
    },
  };
  currentMonth = new Date().getMonth() + 1;
  currentYear = new Date().getFullYear();

  // ── Doughnut chart: Job status distribution ──
  doughnutLabels: string[] = [];
  doughnutDatasets: ChartDataset<'doughnut'>[] = [{ data: [] }];
  doughnutOptions: ChartOptions<'doughnut'> = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '65%',
    plugins: {
      legend: { position: 'bottom', labels: { padding: 16, usePointStyle: true, pointStyle: 'circle' } },
    },
  };

  // ── Bar chart: Monthly job trend ──
  barLabels: string[] = [];
  barDatasets: ChartDataset<'bar'>[] = [{ data: [], label: 'Jobs', borderRadius: 6, backgroundColor: '' }];
  barOptions: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: { grid: { display: false } },
      y: { beginAtZero: true, ticks: { stepSize: 1, precision: 0 } },
    },
    plugins: { legend: { display: false } },
  };

  constructor(
    private api: CommonApiService,
    public auth: LoginService,
    public permissions: Permissions,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.currentUser = this.auth.currentUserValue;
    this.userName = this.currentUser?.user?.name || '';
    const access: string[] = this.currentUser?.user?.role?.access || [];
    this.hasDashboardAccess = access.includes('Dashboard');
    this.isAdmin = this.hasDashboardAccess;

    if (this.hasDashboardAccess) {
      this.loadDashboard();
    }
  }

  get selectedLateDeliveryWindowLabel(): string {
    return this.lateDeliveryWindowOptions.find(
      (option) => option.value === this.selectedLateDeliveryWindow,
    )?.label || 'Last 30 Days';
  }

  get lateDeliveryEmptyStateLabel(): string {
    return this.selectedLateDeliveryWindow === 365
      ? 'No late deliveries in the last 1 year'
      : `No late deliveries in the last ${this.selectedLateDeliveryWindow} days`;
  }

  loadDashboard(): void {
    this.loading = true;
    this.api.getAll('joborder/dashboard_summary', {
      late_days: this.selectedLateDeliveryWindow,
    }).subscribe({
      next: (data: any) => {
        const d = data as DashboardSummary;

        // Counts
        this.doctorCount = d.counts.doctors;
        this.productCount = d.counts.products;
        this.employeeCount = d.counts.employees;
        this.userCount = d.counts.users;
        this.inProductionJobsCount = d.counts.in_production;
        this.holdJobsCount = d.counts.hold;
        this.repairJobsCount = d.counts.repair;
        this.repeatJobsCount = d.counts.repeat;
        this.totalJobsMonth = d.counts.total_jobs_month;
        this.completedMonth = d.counts.completed_month;

        // Revenue
        this.monthlyRevenue = d.monthly_revenue;

        // Deliveries
        this.lateDeliveries = d.deliveries.late;
        this.todayDeliveries = d.deliveries.today;
        this.upcomingDeliveries = d.deliveries.upcoming;
        this.selectedLateDeliveryWindow = d.deliveries.late_window_days || this.selectedLateDeliveryWindow;

        // Top doctors
        this.topDoctors = d.top_doctors;

        // Top products
        this.topProducts = d.top_products || [];
        const totalProductUnits = this.topProducts.reduce(
          (total, product) => total + (Number(product.total_units) || 0),
          0,
        );
        this.productLegendItems = this.topProducts.map((product, index) => ({
          name: product.name,
          units: Number(product.total_units) || 0,
          percentage: totalProductUnits
            ? Math.round(((Number(product.total_units) || 0) / totalProductUnits) * 100)
            : 0,
          color: this.productPalette[index % this.productPalette.length],
        }));

        // Pie chart for top products
        this.productPieLabels = this.topProducts.map(p => p.name);
        this.productPieDatasets = [{
          data: this.topProducts.map(p => p.total_units),
          backgroundColor: this.topProducts.map((_, i) => this.productPalette[i % this.productPalette.length]),
          borderWidth: 0,
        }];

        // Doughnut chart
        const statusLabelMap: Record<string, string> = {
          draft: 'Draft',
          in_production: 'In Production',
          hold: 'On Hold',
          done: 'Completed',
        };
        const statusColorMap: Record<string, string> = {
          draft: '#94a3b8',
          in_production: '#3b82f6',
          hold: '#f59e0b',
          done: '#22c55e',
        };
        const labels: string[] = [];
        const values: number[] = [];
        const colors: string[] = [];
        for (const [status, count] of Object.entries(d.status_distribution)) {
          labels.push(statusLabelMap[status] || status);
          values.push(count);
          colors.push(statusColorMap[status] || '#64748b');
        }
        this.doughnutLabels = labels;
        this.doughnutDatasets = [{ data: values, backgroundColor: colors, borderWidth: 0 }];

        // Bar chart
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        this.barLabels = d.monthly_trend.map(t => {
          const parts = t.month.split('-');
          return monthNames[parseInt(parts[1], 10) - 1] + ' ' + parts[0].slice(-2);
        });
        this.barDatasets = [{
          data: d.monthly_trend.map(t => t.count),
          label: 'Jobs',
          borderRadius: 6,
          backgroundColor: 'color-mix(in srgb, var(--theme-primary) 70%, transparent)',
        }];

        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  onLateDeliveryWindowChange(): void {
    if (!this.hasDashboardAccess) {
      return;
    }
    this.loadDashboard();
  }

  navigate(path: string): void {
    this.router.navigate([path]);
  }

  /** Open the full Doctor Business Summary report for the current month. */
  viewAllTopDoctors(): void {
    this.router.navigate(['/reports/doctorBusinessSummary'], {
      queryParams: { month: this.currentMonth, year: this.currentYear },
    });
  }

  /** Open the full Product Usage Summary report for the current month. */
  viewAllTopProducts(): void {
    this.router.navigate(['/reports/productUsageSummary'], {
      queryParams: { month: this.currentMonth, year: this.currentYear },
    });
  }

  get completionRate(): number {
    if (!this.totalJobsMonth) return 0;
    return Math.round((this.completedMonth / this.totalJobsMonth) * 100);
  }
}
